import React from 'react';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { NavDestination } from './NavigationListItems';

interface IconRailProps {
  items: NavDestination[];
  activeId?: string;
}

// Shared "slightly lighter" overlay for hover and active states.
const HOVER_BG = 'rgba(255, 255, 255, 0.14)';

const Rail = styled('nav')({
  width: 90,
  flexShrink: 0,
  background: '#142F53',
  display: 'flex',
  flexDirection: 'column',
  padding: '8px 0',
  overflowY: 'auto',
});

// Shared rail-item styling. White text/icon is re-asserted across every link
// state because the app-wide styleOverrides.css sets `a`/`:visited` black and
// `a:hover` blue — these class+pseudo selectors out-specify those bare `a` rules.
const railItemStyles = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '5px',
  padding: '11px 6px',
  textDecoration: 'none',
  fontSize: '11px',
  lineHeight: 1.2,
  textAlign: 'center',
  overflowWrap: 'break-word',
  hyphens: 'auto',
  cursor: 'pointer',
  background: 'transparent',
  transition: 'background 0.15s',
  '&, &:link, &:visited, &:focus, &:active': { color: 'rgba(255,255,255,0.72)' },
  '&:hover': { background: HOVER_BG, color: '#fff' },
  '&:focus-visible': {
    outline: 'none',
    boxShadow: '0 0 0 2px #fff, 0 0 0 5px #1976d2',
    borderRadius: '4px',
  },
  '& svg': { fontSize: '24px' },
} as const;

const RailLink = styled(Link, { shouldForwardProp: (prop) => prop !== 'active' })<{ active?: boolean }>(
  ({ active }) => ({
    ...railItemStyles,
    ...(active ? { background: HOVER_BG, '&, &:link, &:visited, &:hover, &:focus, &:active': { color: '#fff' } } : {}),
  }),
);

const RailExternal = styled('a')(railItemStyles);

/**
 * Fixed 90px navy rail of top-level destinations. Internal items are
 * react-router links; the external How-To Guides item opens in a new tab and
 * never carries an active state.
 */
export default function IconRail({ items, activeId }: IconRailProps) {
  return (
    <Rail aria-label="Main navigation">
      {items.map((item) => {
        const Icon = item.icon;
        const isActive = !item.external && item.id === activeId;

        const contents = (
          <>
            <Icon aria-hidden="true" />
            <span>{item.label}</span>
          </>
        );

        if (item.external) {
          return (
            <RailExternal key={item.id} href={item.href} target="_blank" rel="noreferrer" lang="en" title={item.label}>
              {contents}
            </RailExternal>
          );
        }

        return (
          <RailLink
            key={item.id}
            active={isActive}
            to={item.to as string}
            lang="en"
            title={item.label}
            aria-current={isActive ? 'page' : undefined}
          >
            {contents}
          </RailLink>
        );
      })}
    </Rail>
  );
}
